/**
 * Audio processor management hook
 */
import { useCallback, useRef } from "react";

import type * as Comlink from "comlink";
import { debounce } from "lodash";

import type { AppDispatch } from "app/store";

import { createLogger } from "../../logging.util";
import { updateScribeVolume } from "../../medical-scribe.slice";
import { decidePipelineAction } from "../audio-pipeline.util";
import {
  AUDIO_CHANNEL_COUNT,
  AUDIO_SAMPLE_RATE,
  PHANTOM_SESSION_WATCHDOG_MS
} from "../constants";
import type { AudioRefs, RecordingStateRefs, WebSocketRefs } from "../types";

/**
 * AudioWorklet processor code as inline string.
 * This avoids CORS/CSP issues when loading worklet modules in production.
 * The code converts Float32 audio samples to Int16 PCM format and resamples
 * from the native sample rate to the target sample rate (16000 Hz).
 *
 * Note: The processor receives the native and target sample rates via
 * processorOptions during construction.
 */
const createAudioProcessorCode = (
  nativeSampleRate: number,
  targetSampleRate: number
): string => `
class AudioProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    // Get sample rates from processor options
    this.nativeSampleRate = ${nativeSampleRate};
    this.targetSampleRate = ${targetSampleRate};
    this.resampleRatio = this.targetSampleRate / this.nativeSampleRate;

    // Pre-allocated ring buffer for resampling to avoid GC pressure during long recordings
    // Size: enough for ~10 input blocks (128 samples each) = 1280 samples
    // This is much more than needed (typically only a few samples carry over)
    this.ringBufferSize = 2048;
    this.ringBuffer = new Float32Array(this.ringBufferSize);
    this.ringBufferWritePos = 0;  // Where to write next
    this.ringBufferCount = 0;     // Number of valid samples in buffer

    // Fractional read offset for phase continuity across process() calls
    // This maintains the sub-sample position for accurate resampling
    this.fractionalOffset = 0;

    // Pre-allocated output buffer for resampled audio
    // Max output per block: 128 input samples at worst case 1:1 ratio
    this.outputBuffer = new Float32Array(256);

    // Pre-allocated PCM buffer (reused when possible)
    this.pcmBuffer = new Int16Array(256);

    // Listen for flush command to process remaining buffer at end of recording
    this.port.onmessage = (event) => {
      if (event.data && event.data.command === 'flush') {
        this.flushRemainingBuffer();
      }
    };
  }

  // Write samples to ring buffer
  writeToRingBuffer(samples, length) {
    for (let i = 0; i < length; i++) {
      this.ringBuffer[this.ringBufferWritePos] = samples[i];
      this.ringBufferWritePos = (this.ringBufferWritePos + 1) % this.ringBufferSize;
      // Clamp count to prevent overflow - if buffer is full, oldest data is overwritten
      if (this.ringBufferCount < this.ringBufferSize) {
        this.ringBufferCount++;
      }
    }
  }

  // Read sample from ring buffer at absolute index (wraps around)
  readFromRingBufferAt(absoluteIndex) {
    // Calculate position relative to where valid data starts
    // Valid data starts at (writePos - count) and ends at (writePos - 1)
    const startPos = (this.ringBufferWritePos - this.ringBufferCount + this.ringBufferSize) % this.ringBufferSize;
    const pos = (startPos + absoluteIndex) % this.ringBufferSize;
    return this.ringBuffer[pos];
  }

  // Consume samples from ring buffer (just decrease count, data stays in place)
  consumeFromRingBuffer(count) {
    this.ringBufferCount -= count;
  }

  // Flush any remaining samples in the resample buffer
  // Called at end of recording to avoid losing the last bit of audio
  flushRemainingBuffer() {
    if (this.ringBufferCount === 0) {
      this.port.postMessage({ flushed: true });
      return;
    }

    // If sample rates match, just send what's left
    if (this.nativeSampleRate === this.targetSampleRate) {
      const pcmData = new Int16Array(this.ringBufferCount);
      for (let i = 0; i < this.ringBufferCount; i++) {
        const sample = this.readFromRingBufferAt(i);
        const s = Math.max(-1, Math.min(1, sample));
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
    } else {
      // For resampling, we need to process remaining samples
      // Use the current fractionalOffset to continue from where we left off
      const step = 1 / this.resampleRatio;
      let outputCount = 0;

      // Process while we can interpolate
      // For the last sample, use nearest neighbor (same sample) to avoid losing it
      while (this.fractionalOffset < this.ringBufferCount - 0.001) {
        const srcIndexFloor = Math.floor(this.fractionalOffset);
        const srcIndexCeil = Math.min(srcIndexFloor + 1, this.ringBufferCount - 1);
        const fraction = this.fractionalOffset - srcIndexFloor;

        const s0 = this.readFromRingBufferAt(srcIndexFloor);
        const s1 = this.readFromRingBufferAt(srcIndexCeil);
        const sample = s0 * (1 - fraction) + s1 * fraction;

        // Clamp and convert to 16-bit PCM
        const s = Math.max(-1, Math.min(1, sample));
        this.outputBuffer[outputCount++] = s < 0 ? s * 0x8000 : s * 0x7fff;

        this.fractionalOffset += step;
      }

      if (outputCount > 0) {
        const pcmData = new Int16Array(outputCount);
        for (let i = 0; i < outputCount; i++) {
          pcmData[i] = this.outputBuffer[i];
        }
        this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
      }
    }

    // Reset buffer state
    this.ringBufferWritePos = 0;
    this.ringBufferCount = 0;
    this.fractionalOffset = 0;
    this.port.postMessage({ flushed: true });
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const inputData = input[0];

      // If sample rates match, no resampling needed
      if (this.nativeSampleRate === this.targetSampleRate) {
        // Reuse pcmBuffer if large enough, otherwise allocate
        const pcmData = inputData.length <= this.pcmBuffer.length
          ? this.pcmBuffer.subarray(0, inputData.length)
          : new Int16Array(inputData.length);

        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Must create new buffer for transfer (can't transfer subarray)
        const transferBuffer = new Int16Array(inputData.length);
        transferBuffer.set(pcmData.subarray(0, inputData.length));
        this.port.postMessage({ pcmData: transferBuffer.buffer }, [transferBuffer.buffer]);
      } else {
        // Write input samples to ring buffer
        this.writeToRingBuffer(inputData, inputData.length);

        // Step size: how many input samples per output sample
        const step = 1 / this.resampleRatio;
        let outputCount = 0;

        // Generate output samples while we have enough input data
        // We need at least one sample ahead for linear interpolation
        // fractionalOffset maintains phase continuity across process() calls
        while (this.fractionalOffset < this.ringBufferCount - 1) {
          const srcIndexFloor = Math.floor(this.fractionalOffset);
          const srcIndexCeil = srcIndexFloor + 1;
          const fraction = this.fractionalOffset - srcIndexFloor;

          // Linear interpolation between samples
          const s0 = this.readFromRingBufferAt(srcIndexFloor);
          const s1 = this.readFromRingBufferAt(srcIndexCeil);
          const sample = s0 * (1 - fraction) + s1 * fraction;

          // Clamp and convert to 16-bit PCM directly into output buffer
          const s = Math.max(-1, Math.min(1, sample));
          this.outputBuffer[outputCount++] = s < 0 ? s * 0x8000 : s * 0x7fff;

          this.fractionalOffset += step;
        }

        // Consume processed samples from ring buffer
        const samplesConsumed = Math.floor(this.fractionalOffset);
        if (samplesConsumed > 0) {
          this.consumeFromRingBuffer(samplesConsumed);
          // Maintain fractional part for phase continuity
          this.fractionalOffset -= samplesConsumed;
        }

        // Send output if we produced any samples
        if (outputCount > 0) {
          // Must create new Int16Array for transfer
          const pcmData = new Int16Array(outputCount);
          for (let i = 0; i < outputCount; i++) {
            pcmData[i] = this.outputBuffer[i];
          }
          this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
        }
      }
    }

    return true; // Keep processor alive
  }
}

registerProcessor("audio-processor", AudioProcessor);
`;

/**
 * Create a blob URL for the AudioWorklet processor.
 * Using a blob URL ensures the worklet loads from the same origin,
 * avoiding CORS and CSP issues in production deployments.
 */
const createAudioProcessorBlobUrl = (
  nativeSampleRate: number,
  targetSampleRate: number
): string => {
  const blob = new Blob(
    [createAudioProcessorCode(nativeSampleRate, targetSampleRate)],
    {
      type: "application/javascript"
    }
  );
  return URL.createObjectURL(blob);
};

const FFT_SIZE = 2048;
const MIC_THRESHOLD = 0.01;
const CHUNK_SAVE_INTERVAL_MS = 3000; // Save chunks every 3 seconds

/**
 * Create a WAV blob from PCM data
 */
function createWavBlob(pcmData: Int16Array, sampleRate: number): Blob {
  const numChannels = 1; // Mono
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcmData.length * bytesPerSample;
  const bufferSize = 44 + dataSize;

  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);

  // WAV header
  // "RIFF" chunk descriptor
  writeString(view, 0, "RIFF");
  view.setUint32(4, bufferSize - 8, true);
  writeString(view, 8, "WAVE");

  // "fmt " sub-chunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // Sub-chunk size
  view.setUint16(20, 1, true); // Audio format (1 = PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // "data" sub-chunk
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  // Write PCM samples
  let offset = 44;
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < pcmData.length; i++) {
    view.setInt16(offset, pcmData[i], true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    // WAV headers use ASCII encoding, charCodeAt is appropriate here
    view.setUint8(offset + i, string.codePointAt(i) || 0);
  }
}

interface UseAudioProcessorProps {
  dispatch: AppDispatch;
  wsRefs: WebSocketRefs;
  audioRefs: AudioRefs;
  stateRefs: RecordingStateRefs;
  audioWorkerRef: React.MutableRefObject<Comlink.Remote<any> | null>;
}

export const useAudioProcessor = ({
  dispatch,
  wsRefs,
  audioRefs,
  stateRefs,
  audioWorkerRef
}: UseAudioProcessorProps) => {
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const rmsHistoryRef = useRef<number[]>([]);

  // Audio chunk saving refs
  const audioBufferRef = useRef<Int16Array[]>([]);
  const chunkSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef<boolean>(false);
  const pendingSavePromiseRef = useRef<Promise<void> | null>(null);
  /** Tracks the in-flight stopChunkSaving promise so stop-from-paused can await it instead of re-running a concurrent save */
  const pendingChunkSavePromiseRef = useRef<Promise<true> | null>(null);

  // FE-5 observability — detect "phantom" sessions (recording_started received but
  // no audio ever sent). framesSentRef counts binary frames pushed to the WS; the
  // watchdog samples a baseline on session start and warns if it hasn't advanced.
  const framesSentRef = useRef<number>(0);
  const phantomWatchdogTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Analyze audio volume and dispatch updates
   */
  const debouncedAnalyze = useRef(
    debounce(() => {
      const analyser = analyserRef.current;
      if (!analyser) return;

      const dataArray = new Float32Array(analyser.fftSize);
      analyser.getFloatTimeDomainData(dataArray);

      // Calculate RMS
      const sum = dataArray.reduce((acc, val) => acc + val * val, 0);
      const rms = Math.sqrt(sum / dataArray.length);

      // Update RMS history (e.g., last 60 frames ~ 1 second)
      rmsHistoryRef.current.push(rms);
      if (rmsHistoryRef.current.length > 60) {
        rmsHistoryRef.current.shift();
      }

      // Compute moving average and check against threshold
      const avgRms =
        rmsHistoryRef.current.reduce((a, b) => a + b, 0) /
        rmsHistoryRef.current.length;

      // Update volume for visualization
      const volume = Math.min(100, Math.max(0, rms * 1000));

      dispatch(
        updateScribeVolume({
          volume,
          isMicActive: avgRms > MIC_THRESHOLD
        })
      );
    }, 2)
  ).current;

  const analyzeVolume = useCallback(() => {
    if (!stateRefs.isRecording.current) {
      return;
    }
    debouncedAnalyze();
    animationFrameIdRef.current = requestAnimationFrame(analyzeVolume);
  }, [stateRefs, debouncedAnalyze]);

  /**
   * Start volume analysis
   */
  const startVolumeAnalysis = useCallback(() => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }
    animationFrameIdRef.current = requestAnimationFrame(analyzeVolume);
  }, [analyzeVolume]);

  /**
   * Stop volume analysis
   */
  const stopVolumeAnalysis = useCallback(() => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    rmsHistoryRef.current = [];
    // Reset volume to 0
    dispatch(updateScribeVolume({ volume: 0, isMicActive: false }));
  }, [dispatch]);

  /**
   * Save accumulated audio buffer as a chunk
   * Uses a mutex pattern to prevent race conditions
   */
  const saveAudioChunk = useCallback(async () => {
    // If no data or no worker, skip
    if (audioBufferRef.current.length === 0 || !audioWorkerRef.current) {
      return;
    }

    // If a save is already in progress, wait for it to complete then retry
    // We must retry (not return) because new data may have arrived after
    // the in-progress save took its snapshot
    while (isSavingRef.current && pendingSavePromiseRef.current) {
      await pendingSavePromiseRef.current;
      // After waiting, check if there's still data to save
      if (audioBufferRef.current.length === 0 || !audioWorkerRef.current) {
        return;
      }
      // Loop again if another save started while we were waiting
      // This ensures we don't return when there's data that needs saving
    }

    // Mark that we're saving BEFORE taking the snapshot
    isSavingRef.current = true;

    const savePromise = (async () => {
      try {
        // Make a snapshot of the current buffer and clear it immediately
        // This prevents data loss if new chunks arrive during the save
        const buffersToSave = audioBufferRef.current;
        audioBufferRef.current = [];

        // Double-check we have data after taking the lock
        if (buffersToSave.length === 0) {
          return;
        }

        // Concatenate all Int16Array buffers
        const totalLength = buffersToSave.reduce(
          (sum, arr) => sum + arr.length,
          0
        );
        const combined = new Int16Array(totalLength);
        let offset = 0;
        for (const buffer of buffersToSave) {
          combined.set(buffer, offset);
          offset += buffer.length;
        }

        // Convert to WAV blob
        const wavBlob = createWavBlob(combined, AUDIO_SAMPLE_RATE);

        // Save to IndexedDB via worker
        await audioWorkerRef.current!.addChunk(wavBlob);

        createLogger.log(
          `Saved audio chunk: ${wavBlob.size} bytes (${buffersToSave.length} buffers)`,
          stateRefs.recordingStartTime.current
        );
      } catch (error) {
        createLogger.error(
          "Error saving audio chunk:",
          error,
          stateRefs.recordingStartTime.current
        );
      } finally {
        isSavingRef.current = false;
        pendingSavePromiseRef.current = null;
      }
    })();

    pendingSavePromiseRef.current = savePromise;
    await savePromise;
  }, [audioWorkerRef, stateRefs]);

  /**
   * Start periodic chunk saving
   */
  const startChunkSaving = useCallback(() => {
    if (chunkSaveTimerRef.current) {
      clearInterval(chunkSaveTimerRef.current);
    }

    chunkSaveTimerRef.current = setInterval(() => {
      saveAudioChunk();
    }, CHUNK_SAVE_INTERVAL_MS);
  }, [saveAudioChunk]);

  /**
   * Stop chunk saving and save any remaining data
   */
  // Returns true so withTimeout(stopChunkSaving(), ms) returns true | undefined —
  // undefined reliably means the outer timeout fired, not a successful completion.
  // The promise is stored in pendingChunkSavePromiseRef so stop-from-paused can
  // await it instead of racing a second concurrent save over the same buffer.
  const stopChunkSaving = useCallback((): Promise<true> => {
    const p: Promise<true> = (async () => {
      if (chunkSaveTimerRef.current) {
        clearInterval(chunkSaveTimerRef.current);
        chunkSaveTimerRef.current = null;
      }

      // Save any remaining audio data
      // Loop to catch any data that arrives during the save operation
      // This is important because new audio messages can arrive while we're saving
      let saveAttempts = 0;
      const maxAttempts = 5; // Prevent infinite loops

      while (audioBufferRef.current.length > 0 && saveAttempts < maxAttempts) {
        await saveAudioChunk();
        saveAttempts++;

        // Small delay to allow any final messages to arrive
        if (audioBufferRef.current.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      // Final save attempt to catch any stragglers
      if (audioBufferRef.current.length > 0) {
        await saveAudioChunk();
      }

      return true as const;
    })();

    pendingChunkSavePromiseRef.current = p;
    void p.finally(() => {
      if (pendingChunkSavePromiseRef.current === p) {
        pendingChunkSavePromiseRef.current = null;
      }
    });
    return p;
  }, [saveAudioChunk]);

  /**
   * Setup audio pipeline for recording
   */
  const setupAudioPipeline = useCallback(
    async (stream: MediaStream, needsNewSource: boolean) => {
      let audioContext = audioRefs.audioContext.current;
      let processor = audioRefs.processor.current;
      let source = audioRefs.source.current;

      const action = decidePipelineAction({
        hasContext: Boolean(audioContext),
        isContextClosed: audioContext?.state === "closed",
        hasProcessor: Boolean(processor),
        hasSource: Boolean(source),
        needsNewSource
      });

      if (action === "build") {
        const AudioContextClass =
          globalThis.AudioContext ||
          (
            globalThis as typeof globalThis & {
              webkitAudioContext?: typeof AudioContext;
            }
          ).webkitAudioContext;
        if (!AudioContextClass) {
          throw new Error("AudioContext not supported in this browser");
        }

        if (!stream) {
          throw new Error("Audio stream is unavailable");
        }

        // Request the context at the track's reported rate — Firefox requires the
        // AudioContext rate to match the stream's rate. The worklet resamples to target.
        const audioTrack = stream.getAudioTracks()[0];
        const trackSettings = audioTrack?.getSettings();
        const requestedSampleRate = trackSettings?.sampleRate || 48000; // Default to 48kHz if not available

        audioContext = new AudioContextClass({
          sampleRate: requestedSampleRate
        });
        audioRefs.audioContext.current = audioContext;

        // FE-3: the browser may NOT honor the requested sampleRate. A
        // MediaStreamAudioSourceNode delivers audio at the context's ACTUAL rate,
        // so the worklet's resample ratio must be built from audioContext.sampleRate
        // (read back after construction), never from the track value — otherwise
        // later sessions are captured at the wrong timebase and play back distorted.
        const actualSampleRate = audioContext.sampleRate;

        createLogger.log(
          `🎧 Building audio pipeline... (requested: ${requestedSampleRate} Hz, actual context: ${actualSampleRate} Hz, target: ${AUDIO_SAMPLE_RATE} Hz)`,
          stateRefs.recordingStartTime.current
        );

        // Use blob URL to avoid CORS/CSP issues in production
        const processorBlobUrl = createAudioProcessorBlobUrl(
          actualSampleRate,
          AUDIO_SAMPLE_RATE
        );
        try {
          await audioContext.audioWorklet.addModule(processorBlobUrl);
        } finally {
          // Clean up blob URL after module is loaded
          URL.revokeObjectURL(processorBlobUrl);
        }

        source = audioContext.createMediaStreamSource(stream);
        audioRefs.source.current = source;

        processor = new AudioWorkletNode(audioContext, "audio-processor");
        audioRefs.processor.current = processor;

        // Create analyser for volume tracking
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        analyserRef.current = analyser;

        // Connect: source -> processor (for recording)
        //          source -> analyser (for volume visualization)
        source.connect(processor);
        source.connect(analyser);
      } else if (action === "rebuild-source") {
        // FE-4: reuse the live AudioContext + worklet across pause→resume. Keeping
        // one context means one stable sample rate (no resample-ratio drift) and no
        // leaked contexts. Only the mic source is stale (its track was stopped on
        // pause), so recreate just that node on the existing context.
        if (!audioContext || !processor) {
          throw new Error(
            "Audio pipeline reuse requested without a live context"
          );
        }

        if (source) {
          try {
            source.disconnect();
          } catch (err) {
            createLogger.error(
              "⚠️ Error disconnecting stale mic source on rebuild",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        }

        source = audioContext.createMediaStreamSource(stream);
        audioRefs.source.current = source;
        source.connect(processor);
        if (analyserRef.current) {
          source.connect(analyserRef.current);
        }

        createLogger.log(
          "🔄 Rebuilt mic source on existing audio pipeline",
          stateRefs.recordingStartTime.current
        );
      } else {
        createLogger.log(
          "♻️ Reusing existing audio pipeline",
          stateRefs.recordingStartTime.current
        );
      }

      if (!processor) {
        throw new Error("Audio processor failed to initialize");
      }

      // FE-2: a freshly created context can start suspended under the browser's
      // autoplay policy, and pauseAudioGraph() suspends the context on pause —
      // resume it so the worklet actually receives audio. (Previously only the
      // reuse branch resumed, so a new/paused context could stay silent.)
      if (audioContext && audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Handle messages from the AudioWorklet processor
      // IMPORTANT: Remove any existing handler to prevent memory leaks
      // when pipeline is reused (e.g., when resuming recording)
      processor.port.onmessage = null;
      processor.port.onmessage = (event) => {
        // Check recording state to prevent processing stale audio data
        if (!event.data.pcmData || !stateRefs.isRecording.current) {
          return;
        }

        const currentWs = wsRefs.ws.current;

        // Send to WebSocket for real-time transcription
        if (currentWs && currentWs.readyState === WebSocket.OPEN) {
          try {
            currentWs.send(event.data.pcmData);
            framesSentRef.current += 1; // FE-5: phantom-session watchdog counter
          } catch (err) {
            createLogger.error(
              "❌ Error sending audio data to WebSocket",
              err,
              stateRefs.recordingStartTime.current
            );
            // Don't throw - continue recording even if one packet fails
          }
        }

        // Also accumulate for chunk saving to IndexedDB
        const pcmData = new Int16Array(event.data.pcmData);
        audioBufferRef.current.push(pcmData);
      };
    },
    [audioRefs, wsRefs, stateRefs]
  );

  /**
   * Get microphone stream
   */
  const getMicrophoneStream = useCallback(async (): Promise<{
    stream: MediaStream;
    needsNewStream: boolean;
  }> => {
    let stream = audioRefs.stream.current;
    let needsNewStream = false;

    if (stream) {
      const hasEndedTrack = stream
        .getTracks()
        .some((track) => track.readyState === "ended");
      needsNewStream = hasEndedTrack;
    } else {
      needsNewStream = true;
    }

    if (needsNewStream) {
      // Note: We don't request a specific sample rate here because browsers
      // may not honor it (especially Firefox). Instead, we get the native
      // sample rate and resample in the AudioWorklet to the target rate.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: AUDIO_CHANNEL_COUNT
        }
      });
      audioRefs.stream.current = stream;
    }

    return { stream: stream!, needsNewStream };
  }, [audioRefs]);

  /**
   * Stop media stream tracks to stop audio input.
   * Call this BEFORE stopping recording to ensure all audio is captured.
   */
  const stopMediaStream = useCallback(() => {
    if (audioRefs.stream.current) {
      try {
        for (const track of audioRefs.stream.current.getTracks()) {
          track.stop();
        }
        createLogger.log(
          "🛑 Media stream tracks stopped",
          stateRefs.recordingStartTime.current
        );
      } catch (err) {
        createLogger.error(
          "⚠️ Error stopping stream tracks",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.stream.current = null;
    }
  }, [audioRefs, stateRefs]);

  /**
   * Flush the audio worklet's remaining buffer.
   * Call this after stopping the media stream to capture any remaining samples.
   */
  // Returns true on success so withTimeout(flushAudioWorklet(), ms) returns
  // true | undefined — undefined reliably means the outer timeout fired.
  // (Promise<void> resolves as undefined, making success indistinguishable from timeout.)
  const flushAudioWorklet = useCallback((): Promise<true> => {
    return new Promise((resolve) => {
      const processor = audioRefs.processor.current;
      if (!processor) {
        resolve(true);
        return;
      }

      // Set up a one-time listener for the flush confirmation
      const originalOnMessage = processor.port.onmessage;
      // Firefox needs more time to process the flush than Chrome
      const timeoutId = setTimeout(() => {
        // Restore original handler and resolve after timeout
        processor.port.onmessage = originalOnMessage;
        createLogger.warn(
          "⚠️ Flush timeout - continuing without confirmation",
          stateRefs.recordingStartTime.current
        );
        resolve(true);
      }, 1500); // 1500ms timeout (increased for Firefox compatibility)

      processor.port.onmessage = (event) => {
        // Handle PCM data - during flush we ALWAYS capture data
        // regardless of isRecording state to ensure nothing is lost
        if (event.data.pcmData) {
          // Send to WebSocket if still connected
          const currentWs = wsRefs.ws.current;
          if (currentWs && currentWs.readyState === WebSocket.OPEN) {
            try {
              currentWs.send(event.data.pcmData);
            } catch (err) {
              // Ignore send errors during flush
            }
          }
          // Always add to buffer during flush - this is critical for Firefox
          const pcmData = new Int16Array(event.data.pcmData);
          audioBufferRef.current.push(pcmData);
        }

        // Handle flush confirmation
        if (event.data.flushed) {
          clearTimeout(timeoutId);
          processor.port.onmessage = originalOnMessage;
          createLogger.log(
            "✅ Audio worklet buffer flushed",
            stateRefs.recordingStartTime.current
          );
          resolve(true);
        }
      };

      // Send flush command to worklet
      processor.port.postMessage({ command: "flush" });
    });
  }, [audioRefs, stateRefs, wsRefs]);

  /**
   * Cancel a pending phantom-session watchdog (FE-5).
   */
  const clearPhantomSessionWatchdog = useCallback(() => {
    if (phantomWatchdogTimerRef.current) {
      clearTimeout(phantomWatchdogTimerRef.current);
      phantomWatchdogTimerRef.current = null;
    }
  }, []);

  /**
   * Arm the phantom-session watchdog (FE-5). Called when the backend confirms
   * `recording_started`. If no audio frames are sent within the window, the
   * session started but is silent — surface a high-signal warning so a phantom
   * session (the root symptom of the resume bug) is observable in production.
   */
  const armPhantomSessionWatchdog = useCallback(() => {
    clearPhantomSessionWatchdog();
    const baseline = framesSentRef.current;
    phantomWatchdogTimerRef.current = setTimeout(() => {
      phantomWatchdogTimerRef.current = null;
      if (framesSentRef.current === baseline) {
        createLogger.warn(
          `🚨 Phantom session: recording_started received but no audio frames sent in ${PHANTOM_SESSION_WATCHDOG_MS}ms`,
          stateRefs.recordingStartTime.current
        );
      }
    }, PHANTOM_SESSION_WATCHDOG_MS);
  }, [clearPhantomSessionWatchdog, stateRefs]);

  /**
   * Pause the audio graph without destroying it (FE-4).
   *
   * Disconnects the stale mic source (its track was stopped on pause) and
   * suspends the context to release CPU, but keeps the AudioContext + worklet +
   * analyser alive so the next resume reuses them. Reusing one context keeps the
   * worklet's resample ratio stable across the whole visit and avoids leaking
   * contexts — the root cause of both pause/resume bugs.
   */
  const pauseAudioGraph = useCallback(async () => {
    clearPhantomSessionWatchdog();

    if (audioRefs.source.current) {
      try {
        audioRefs.source.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error disconnecting mic source on pause",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.source.current = null;
    }

    const audioContext = audioRefs.audioContext.current;
    if (audioContext && audioContext.state === "running") {
      try {
        await audioContext.suspend();
      } catch (err) {
        createLogger.error(
          "⚠️ Error suspending audio context on pause",
          err,
          stateRefs.recordingStartTime.current
        );
      }
    }
  }, [audioRefs, clearPhantomSessionWatchdog, stateRefs]);

  /**
   * Cleanup audio resources
   */
  const cleanupAudioResources = useCallback(async () => {
    // Stop volume analysis
    stopVolumeAnalysis();
    clearPhantomSessionWatchdog();

    // Stop chunk saving and save remaining data
    await stopChunkSaving();

    // Clear any remaining buffers
    audioBufferRef.current = [];
    isSavingRef.current = false;
    pendingSavePromiseRef.current = null;

    // Disconnect analyser
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error disconnecting analyser",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      analyserRef.current = null;
    }

    // Stop audio processor
    if (audioRefs.processor.current) {
      try {
        // Remove message handler before closing to prevent memory leaks
        audioRefs.processor.current.port.onmessage = null;
        audioRefs.processor.current.port.close();
        audioRefs.processor.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error closing processor",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.processor.current = null;
    }

    if (audioRefs.source.current) {
      try {
        audioRefs.source.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error disconnecting source node",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.source.current = null;
    }

    // Close audio context
    if (audioRefs.audioContext.current) {
      try {
        await audioRefs.audioContext.current.close();
      } catch (err) {
        createLogger.error(
          "⚠️ Error closing audio context",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.audioContext.current = null;
    }

    // Note: Media stream tracks should already be stopped by stopMediaStream()
    // but clean up the reference just in case
    audioRefs.stream.current = null;
  }, [
    audioRefs,
    stateRefs,
    stopVolumeAnalysis,
    stopChunkSaving,
    clearPhantomSessionWatchdog
  ]);

  /**
   * Disconnect and release AudioContext nodes without calling stopChunkSaving().
   * Use when the pause path already started stopChunkSaving — this avoids a
   * second concurrent IDB write while the first is still draining the buffer.
   */
  const teardownAudioNodes = useCallback(async () => {
    stopVolumeAnalysis();
    clearPhantomSessionWatchdog();

    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error disconnecting analyser",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      analyserRef.current = null;
    }

    if (audioRefs.processor.current) {
      try {
        audioRefs.processor.current.port.onmessage = null;
        audioRefs.processor.current.port.close();
        audioRefs.processor.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error closing processor",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.processor.current = null;
    }

    if (audioRefs.source.current) {
      try {
        audioRefs.source.current.disconnect();
      } catch (err) {
        createLogger.error(
          "⚠️ Error disconnecting source node",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.source.current = null;
    }

    if (audioRefs.audioContext.current) {
      try {
        await audioRefs.audioContext.current.close();
      } catch (err) {
        createLogger.error(
          "⚠️ Error closing audio context",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      audioRefs.audioContext.current = null;
    }

    audioRefs.stream.current = null;
  }, [audioRefs, stateRefs, stopVolumeAnalysis, clearPhantomSessionWatchdog]);

  return {
    setupAudioPipeline,
    getMicrophoneStream,
    cleanupAudioResources,
    teardownAudioNodes,
    pauseAudioGraph,
    pendingChunkSavePromiseRef,
    startVolumeAnalysis,
    stopVolumeAnalysis,
    startChunkSaving,
    stopChunkSaving,
    stopMediaStream,
    flushAudioWorklet,
    armPhantomSessionWatchdog,
    clearPhantomSessionWatchdog
  };
};
